import React, { useMemo } from 'react';
import { Button, message, Modal, Tooltip } from 'antd';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import AssetPreview from './components/AssetPreview';
import StagePreviewCard from './components/StagePreviewCard';
import { toggleReachedEntityLimitModal } from '@redux/actions/modalAction';
import { ApprovalWorkflowItemDto, AWAssetDto, AWTemplateDto } from '@api/Api';
import {
  useAWTemplateList,
  useUpdateTemplate
} from '@hooks/queries/awTemplate';
import {
  useApprovalWorkflowSplit,
  useCreateApprovalWorkflow,
  useUpdateApprovalWorkflow
} from '@hooks/queries/approval';
import { useCurrentWorkspace } from '@hooks/workspace';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import SaveTemplate from '@components/Modals/ApprovalModal/ApprovalModal/components/ManageTemplate/SaveTemplate';
import UpdateTemplate from '@components/Modals/ApprovalModal/ApprovalModal/components/ManageTemplate/UpdateTemplate';
import type {
  AwModeState,
  AwState
} from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import { diffApproval } from '@components/Modals/ApprovalModal/ApprovalModal/utils';
import type { StepItemKey } from '@components/Modals/ApprovalModal/ApprovalModal/components/Steps/Steps';
import { CrossIcon, DiamondUpgradeIcon, SuccessIcon } from '@icons';
import './FinalReviewStep.less';

const executeHandlerCopies: Record<
  AwModeState,
  { title: string; text: string }
> = {
  create: {
    title: 'Launch Approval Workflow',
    text: 'Are you sure you want to proceed with launching the approval routing?'
  },
  'aw-split-copy': {
    title: 'Launch Approval Workflow',
    text: 'Are you sure you want to proceed with launching the approval routing?'
  },
  edit: {
    title: 'Update Approval Workflow',
    text: 'Are you sure you want to update the approval workflow?'
  },
  'create-template': {
    title: 'Save Approval Template',
    text: 'Are you sure you want to save the template?'
  },
  'edit-template': {
    title: 'Update Approval Template',
    text: 'Are you sure you want to update the template?'
  }
};

type FinalReviewStepProps = {
  awMode: AwModeState;
  awState: AwState;
  selectedAssetVersions: AWAssetDto[];
  ownerEmail: string;
  approvalWorkflow?: ApprovalWorkflowItemDto;
  approvalWorkflowSplit?: ApprovalWorkflowItemDto;
  selectedTemplate: AWTemplateDto | null;
  onChangeStep: (value: StepItemKey) => void;
  sendMixpanelEvent: () => void;
  onCloseModal: () => void;
  onResetModal: () => void;
  callback?: (aw: ApprovalWorkflowItemDto) => void;
};

export default function FinalReviewStep({
  awMode,
  awState,
  ownerEmail,
  approvalWorkflow,
  approvalWorkflowSplit,
  selectedAssetVersions,
  selectedTemplate,
  onChangeStep,
  sendMixpanelEvent,
  onCloseModal,
  onResetModal,
  callback
}: FinalReviewStepProps) {
  const dispatch = useDispatch();
  const { tourData } = useOnboardingProcess();
  const { currentOrganization } = useOrganization();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const [modal, contextHolder] = Modal.useModal();
  const isCreateAw = awMode === 'create';
  const isEditAw = awMode === 'edit' && !!approvalWorkflow;
  const isCreateTemplate = awMode === 'create-template';
  const isEditTemplate = awMode === 'edit-template' && !!selectedTemplate;
  const isSplitAw =
    !!approvalWorkflowSplit && !isEditTemplate && !isCreateTemplate;
  const { stages } = awState;

  const canUseApprovalTemplates =
    !!currentOrganization.entity?.features.approvalTemplates;
  const canCreateTemplate =
    canUseApprovalTemplates &&
    currentWorkspace?.permissions?.manageApprovalTemplates &&
    !selectedTemplate?.id &&
    !isEditAw;
  const canUpdateTemplate =
    canUseApprovalTemplates &&
    currentWorkspace?.permissions?.manageApprovalTemplates &&
    !!approvalWorkflow?.templateId &&
    !!isEditAw;

  const {
    mutateAsync: createApprovalWorkflow,
    isPending: isLoadingCreateApprovalWorkflow
  } = useCreateApprovalWorkflow({});

  const {
    mutateAsync: createApprovalWorkflowSplit,
    isPending: isLoadingApprovalWorkflowSplit
  } = useApprovalWorkflowSplit({});

  const {
    mutateAsync: updateApprovalWorkflow,
    isPending: isLoadingUpdateApprovalWorkflow
  } = useUpdateApprovalWorkflow({});

  const { mutateAsync: updateTemplate, isPending: isLoadingUpdateTemplate } =
    useUpdateTemplate({});

  const { data: templatesData } = useAWTemplateList({
    enabled: canUseApprovalTemplates,
    workspaceId: currentWorkspace?.id || ''
  });

  const limitTemplateReached = useMemo(() => {
    if (!currentOrganization?.entity?.limitsV2.awTemplates) return false;
    const countTemplatePerPage = (
      templatesData?.pages.flatMap((x) => x.edges.map((x) => x.node)) ?? []
    ).length;
    return (
      countTemplatePerPage >= currentOrganization?.entity?.limitsV2.awTemplates
    );
  }, [templatesData, currentOrganization]);

  const onCreateAw = async ({ templateId }: { templateId?: string }) => {
    try {
      if (!awState) return;
      const { restart, ...awStateValues } = awState;
      sendMixpanelEvent();
      const assetIds = selectedAssetVersions.map((i) => i.id);
      let res: any;
      if (isSplitAw) {
        res = await createApprovalWorkflowSplit({
          id: approvalWorkflowSplit.id,
          assetIds,
          settings:
            awMode === 'aw-split-copy'
              ? {
                  name: awState.name,
                  lockDecisions: awState.lockDecisions,
                  disableNonApprovedDownloads:
                    awState.disableNonApprovedDownloads,
                  newVersionBehavior: awState.newVersionBehavior,
                  password: awState.password,
                  customMessage: awState.customMessage,
                  allowAllVersions: awState.allowAllVersions,
                  allowAllComments: awState.allowAllComments,
                  type: 'copy',
                  restart: !!restart
                }
              : {
                  ...awStateValues,
                  templateId: templateId || null,
                  type: 'new'
                }
        });
      } else {
        if (awMode === 'create') {
          res = await createApprovalWorkflow({
            assetIds,
            templateId: templateId || null,
            ...awStateValues
          });
        }
      }

      const newApprovalWorkflow = res?.data?.approvalWorkflow;

      message.success({
        key: 'aw-create',
        content: (
          <div className="message-dark-modal-content">
            <div>
              <div>Success</div>
              <div className="message-dark-modal-text">
                {awState.name} approval routing links have been sent to
                reviewers.
              </div>
            </div>
            <CrossIcon
              onClick={() => message.destroy('aw-create')}
              className="message-dark-modal-close-btn"
            />
          </div>
        ),
        className: 'message-dark-modal b-with-button',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
      onCloseModal();
      callback?.(newApprovalWorkflow);
    } catch (e: any) {
      message.error(
        e.description || e?.response?.data?.message || 'Something went wrong'
      );
    }
  };

  const onUpdateAw = async () => {
    if (isLoadingUpdateApprovalWorkflow || !approvalWorkflow?.id) return;
    try {
      sendMixpanelEvent();
      const getDiffApproval = diffApproval({
        currentAW: approvalWorkflow,
        updateAW: awState,
        selectedAssetVersions
      });
      const { data } = await updateApprovalWorkflow(getDiffApproval);
      onCloseModal();
      callback?.(data.approvalWorkflow);
    } catch (err) {
      const errorCode = (err as any).response?.data?.feature;
      let entity = 'unknown';
      if (errorCode === 'aw_stages_limit') entity = 'stages';
      if (errorCode === 'aw_stage_members_limit') entity = 'stage members';
      onCloseModal();
      dispatch(
        toggleReachedEntityLimitModal({ visible: true, entity: entity as any })
      );
    }
  };

  const onUpdateTemplate = async () => {
    try {
      if (!currentWorkspace?.id) return;
      if (!selectedTemplate) return;
      const data = {
        workspaceId: currentWorkspace.id,
        name: selectedTemplate.name,
        lockDecisions: awState.lockDecisions,
        disableNonApprovedDownloads: awState.disableNonApprovedDownloads,
        newVersionBehavior: awState.newVersionBehavior,
        password: awState.password,
        customMessage: awState.customMessage,
        allowAllVersions: awState.allowAllVersions,
        allowAllComments: awState.allowAllComments,
        stages: (awState.stages || []).map((item) => ({
          name: item.name,
          completionCondition: item.completionCondition,
          members: item.members.map((member) => ({
            email: member.email,
            canComment: member.canComment,
            canSubmitDecision: member.canSubmitDecision,
            canUploadNewVersion: member.canUploadNewVersion,
            canManageAW: member.canManageAW,
            canDownload: member.canDownload,
            hasCommentPriority: member.hasCommentPriority,
            hasDominatingDecision: member.hasDominatingDecision
          }))
        }))
      };
      await updateTemplate({
        id: selectedTemplate.id,
        ...data
      });
      onResetModal();
    } catch (e: any) {
      message.error({
        content:
          e.description ||
          e?.response?.data?.message ||
          'Failed to update template'
      });
    }
  };

  const handlerTemplate = async () => {
    if (!awState) return;

    const onFinish = (templateId?: string) => {
      if (awMode === 'create-template') {
        onResetModal();
        return;
      }
      if (awMode === 'edit') {
        onUpdateAw();
        return;
      }
      onCreateAw({ templateId });
      onCloseModal();
    };

    if (canCreateTemplate && !limitTemplateReached) {
      const templateModal = modal.confirm({
        focusTriggerAfterClose: true,
        zIndex: 1010,
        width: 474,
        icon: null,
        className: 'manage-template',
        centered: true,
        closable: true,
        maskClosable: true,
        closeIcon: <CrossIcon />,
        content: (
          <SaveTemplate
            awState={awState}
            currentWorkspace={currentWorkspace}
            onCancel={() => templateModal.destroy()}
            initialValues={{
              saveAsTemplate: awMode === 'create-template' ? 'yes' : 'no',
              templateName: ''
            }}
            onFinish={(templateId) => {
              templateModal.destroy();
              onFinish(templateId);
            }}
          />
        )
      });
      return;
    }
    if (canUpdateTemplate) {
      const templateModal = modal.confirm({
        focusTriggerAfterClose: true,
        zIndex: 1010,
        width: 474,
        icon: null,
        className: 'manage-template',
        centered: true,
        closable: true,
        maskClosable: true,
        closeIcon: <CrossIcon />,
        content: (
          <UpdateTemplate
            awState={awState}
            currentWorkspace={currentWorkspace}
            limitTemplateReached={limitTemplateReached}
            onCancel={() => templateModal.destroy()}
            templateId={approvalWorkflow?.templateId || ''}
            onFinish={(templateId) => {
              templateModal.destroy();
              onFinish(templateId);
            }}
          />
        )
      });
      return;
    }
    onFinish(selectedTemplate?.id);
  };

  const executeHandler = (callback: () => void) => {
    const invalidStages = stages.filter((i) => {
      return !(i.members || []).some((member) => member.canSubmitDecision);
    });
    if (invalidStages.length) {
      const { title, text } = executeHandlerCopies[awMode];
      Modal.confirm({
        onOk: callback,
        width: 500,
        icon: null,
        centered: true,
        okText: 'Yes',
        cancelText: 'No',
        closable: true,
        maskClosable: true,
        closeIcon: <CrossIcon />,
        zIndex: 1100,
        okButtonProps: {
          style: { minWidth: 124, height: 48, padding: '8px 15px' }
        },
        cancelButtonProps: {
          style: {
            marginRight: '16px',
            border: 'none',
            height: 48,
            padding: '8px 32px'
          }
        },
        content: (
          <div className="modal_container">
            <h2
              className="modal_container__title"
              style={{ fontSize: 22, marginBottom: '0.5em' }}
            >
              {title}
            </h2>
            <p className="modal_container__subtitle">
              You haven’t added reviewers to{' '}
              <strong>
                Stage{invalidStages.length !== 1 && 's'}{' '}
                {invalidStages
                  .map((invalidStage) => stages.indexOf(invalidStage) + 1)
                  .join(', ')}
                .
              </strong>{' '}
              {text}
            </p>
          </div>
        )
      });
      return;
    }
    callback();
  };

  return (
    <div className="approval-step final-review-step">
      {contextHolder}
      <div className="approval-step_head">
        <h2 className="approval-step_title">
          Final&#160;review
          <Tooltip
            title={`${awState.name} approval routing`}
            placement="topLeft"
            overlayClassName="approval-modal-tooltip"
            className="approval-step_route-name"
          >
            {awState.name} approval routing
          </Tooltip>
        </h2>
        <div className="approval-step_text">
          You can review your choices at every step and edit them if necessary
        </div>
      </div>
      <div className="approval-step_wrapper">
        {(isCreateAw || isEditAw || isSplitAw) && (
          <div className="final-review-step_section tour-route-for-approval-final-assets">
            <div className="final-review-step_head">
              <div className="final-review-step_head-name">
                Media
                <div className="final-review-step_head-count">
                  {selectedAssetVersions.length}
                </div>
              </div>
              <div
                className="final-review-step_head-action"
                onClick={() => onChangeStep('media')}
              >
                Edit
              </div>
            </div>
            <div className="final-review-step_assets">
              {selectedAssetVersions.map((item) => (
                <AssetPreview key={item.id} asset={item} />
              ))}
            </div>
          </div>
        )}

        <div className="final-review-step_section tour-route-for-approval-final-stages">
          <div className="final-review-step_head">
            <div className="final-review-step_head-name">
              Stages
              <div className="final-review-step_head-count">
                {stages.length}
              </div>
            </div>
            <div
              className="final-review-step_head-action"
              onClick={() => onChangeStep('stages')}
            >
              Edit
            </div>
          </div>
          <div className="final-review-step_stages">
            {stages.map((item, index) => (
              <div key={item.id} className="final-review-step_stages-block">
                <StagePreviewCard
                  stage={item}
                  stageNumber={index + 1}
                  ownerEmail={ownerEmail || ''}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="approval-step_buttons">
        <div
          className={classNames(
            'final-review-step_buttons tour-route-for-approval-final-buttons',
            {
              'b-outline': tourData?.type === 'route-for-approval'
            }
          )}
        >
          <Button type="text" onClick={onCloseModal}>
            Cancel
          </Button>
          {(isCreateAw || isSplitAw) && (
            <>
              <UpgradePlanTooltip
                text={
                  limitTemplateReached
                    ? 'to enterprise plan to unlock additional approval routing templates.'
                    : 'to a Team and Enterprise plan to unlock approval routing templates.'
                }
                isCanVisible={limitTemplateReached || !canUseApprovalTemplates}
                placement="top"
                innerStyle={{
                  maxWidth: '280px'
                }}
              >
                <div>
                  <Button
                    type="ghost"
                    icon={
                      limitTemplateReached || !canUseApprovalTemplates ? (
                        <DiamondUpgradeIcon className="upgrade-icon" />
                      ) : undefined
                    }
                    style={{
                      minWidth: 152,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4
                    }}
                    onClick={() => executeHandler(handlerTemplate)}
                    disabled={
                      isLoadingCreateApprovalWorkflow ||
                      isLoadingApprovalWorkflowSplit ||
                      limitTemplateReached ||
                      !canUseApprovalTemplates
                    }
                  >
                    Save and launch
                  </Button>
                </div>
              </UpgradePlanTooltip>
              <Button
                type="primary"
                loading={
                  isLoadingCreateApprovalWorkflow ||
                  isLoadingApprovalWorkflowSplit
                }
                onClick={() =>
                  executeHandler(() =>
                    onCreateAw({ templateId: selectedTemplate?.id })
                  )
                }
                style={{
                  minWidth: 152
                }}
              >
                Launch now
              </Button>
            </>
          )}
          {isCreateTemplate && (
            <UpgradePlanTooltip
              text={
                limitTemplateReached
                  ? 'to enterprise plan to unlock additional approval routing templates.'
                  : 'to a Team and Enterprise plan to unlock approval routing templates.'
              }
              isCanVisible={limitTemplateReached || !canUseApprovalTemplates}
              placement="top"
              innerStyle={{
                maxWidth: '280px'
              }}
            >
              <Button
                type="primary"
                icon={
                  limitTemplateReached || !canUseApprovalTemplates ? (
                    <DiamondUpgradeIcon className="upgrade-icon" />
                  ) : undefined
                }
                style={{
                  minWidth: 152,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
                onClick={() => executeHandler(handlerTemplate)}
              >
                Save
              </Button>
            </UpgradePlanTooltip>
          )}
          {isEditAw && (
            <Button
              type="primary"
              onClick={() => executeHandler(handlerTemplate)}
              loading={isLoadingUpdateApprovalWorkflow}
              style={{
                minWidth: 152
              }}
            >
              Update
            </Button>
          )}
          {isEditTemplate && (
            <Button
              type="primary"
              onClick={() => executeHandler(onUpdateTemplate)}
              loading={isLoadingUpdateTemplate}
              style={{
                minWidth: 152
              }}
            >
              Update
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
